import { useMemo } from 'react'
import { SearchableSelect, type SearchOption } from '@/components/SearchableSelect'
import { categorySections } from '@/lib/category-groups'
import type { MaterialCategory } from '@/lib/types'

/**
 * A category select with a search box at the top of its list (the catalog has many categories).
 * Matches the name, its translations and the group; the list is split by group like the categories
 * page, with «— не задана —» first.
 */
export function CategoryPicker({
  categories,
  selectedId,
  buttonLabel,
  onSelect,
  labelledBy,
  ariaLabel,
}: {
  /** In display order. */
  categories: readonly MaterialCategory[]
  /** The selected category id; `''` = none; `null` = a value the catalog does not know (legacy). */
  selectedId: string | null
  /** What the closed control shows. */
  buttonLabel: string
  /** `''` = «не задана». */
  onSelect: (id: string) => void
  labelledBy?: string
  /** Accessible name when there is no visible label to point at. */
  ariaLabel?: string
}) {
  const options = useMemo<SearchOption[]>(() => {
    const sections = categorySections(categories)
    const grouped = sections.some((section) => section.name !== null)
    return sections.flatMap((section) =>
      section.categories.map((item) => ({
        id: item.id,
        label: item.name,
        group: grouped ? (section.name ?? 'Без группы') : null,
        search: [item.name, item.groupName, ...Object.values(item.nameI18n)].join(' '),
      })),
    )
  }, [categories])
  return (
    <SearchableSelect
      options={options}
      noneLabel="— не задана —"
      selectedId={selectedId}
      buttonLabel={buttonLabel}
      onSelect={onSelect}
      labelledBy={labelledBy}
      ariaLabel={ariaLabel}
      searchPlaceholder="Поиск категории"
      listLabel="Категории"
      // Categories are few enough to show all.
      limit={Number.POSITIVE_INFINITY}
    />
  )
}
